import { type FC, useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash-es';

import {
  asSearchItems,
  type SearchItemEntity,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import { searchAssetToRelate } from '../../../../ducks/asset_relations/actions';
import { useAppDispatch } from '../../../../store/react_redux_typed';
import LoadingBar from '../../../../ui/loading_bar/loading_bar';
import SearchInput from '../../../../ui/search_input/search_input';
import CreateRelationsTable from '../table';

import styles from '../create_relations.module.css';

type Props = {
  id: string;
  selectedItems: string[];
  onSearch: (items: SearchItemEntity[]) => void;
  onSelect: (id: string, value: boolean) => void;
};

const CreateRelationSearch: FC<Props> = ({
  id,
  onSearch,
  onSelect,
  selectedItems,
}) => {
  const [items, setItems] = useState<SearchItemEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const search = useCallback(
    (query?: string): void => {
      setLoading(true);
      dispatch(searchAssetToRelate(id, query)).then((response) => {
        const objects = asSearchItems(response.body.objects);
        setItems(objects);
        onSearch(objects);
        setLoading(false);
      });
    },
    [id, onSearch],
  );

  const onSearchDebounce = useCallback(debounce(search, 400), []);

  useEffect(() => {
    search();
    return () => {
      onSearchDebounce.cancel();
    };
  }, [onSearchDebounce, search]);

  return (
    <div className={styles.search}>
      <div className={styles.field}>
        <SearchInput onSearch={onSearchDebounce} />
      </div>
      <div className={styles.table}>
        {loading && <LoadingBar />}
        {!loading && (
          <CreateRelationsTable
            items={items}
            selectedItems={selectedItems}
            onSelect={onSelect}
          />
        )}
      </div>
    </div>
  );
};

export default CreateRelationSearch;
